/**
 * Treat — re-run the BUCKS currency converter after the page changes itself.
 *
 * BUCKS (Helixo) converts every price wrapped in <span class="money"> by the store's money
 * format when the page loads. Prices the theme writes later — cart drawer updates, variant
 * changes, predictive search results, card swatches — arrive after that pass and would stay in
 * SAR. Helixo's documented fix is to call bucksCC.reConvert() after such updates.
 *
 * Rather than wiring a call into every script, this watches the DOM for newly inserted nodes
 * that contain a .money element and re-runs the converter once per burst. It does nothing at all
 * when the app is not installed (no bucksCC on window), so it is safe to ship before the app is
 * enabled.
 *
 * It also keeps the BUCKS cart notice ("Checkout is in SAR") in the cart drawer. The app fills
 * <div class="buckscc-cart-banner"> once; adding to cart re-renders the whole drawer and leaves an
 * empty anchor behind. The app's last output is remembered and copied into any empty anchor.
 */
(() => {
  let timer = null;
  let converting = false;
  let pendingDuringConvert = false;

  const convert = (isFollowUp = false) => {
    const bucks = window.bucksCC;
    if (!bucks || typeof bucks.reConvert !== 'function') return;
    converting = true;
    try {
      bucks.reConvert();
    } catch (error) {
      console.warn('[treat-currency] reConvert failed', error);
    }
    // The converter rewrites the price nodes it just found; ignore that echo. Prices that arrived
    // DURING the window (the cart drawer renders twice after an add) get exactly one follow-up
    // pass, so they are not left in SAR, and the follow-up cannot re-arm itself into a loop.
    window.setTimeout(() => {
      converting = false;
      const needsFollowUp = pendingDuringConvert && !isFollowUp;
      pendingDuringConvert = false;
      if (needsFollowUp) convert(true);
    }, 100);
  };

  const hasMoney = (node) =>
    node.nodeType === Node.ELEMENT_NODE &&
    (node.matches('.money') || node.querySelector('.money') !== null);

  let bannerHTML = '';
  const syncBanners = () => {
    const banners = document.querySelectorAll('.buckscc-cart-banner');
    banners.forEach((banner) => {
      if (banner.childElementCount > 0) bannerHTML = banner.innerHTML;
    });
    if (!bannerHTML) return;
    banners.forEach((banner) => {
      if (banner.childElementCount === 0) banner.innerHTML = bannerHTML;
    });
  };

  const observer = new MutationObserver((mutations) => {
    if (!window.bucksCC) return;
    syncBanners();
    if (converting) {
      if (mutations.some((m) => Array.from(m.addedNodes).some(hasMoney))) pendingDuringConvert = true;
      return;
    }
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (hasMoney(node)) {
          window.clearTimeout(timer);
          timer = window.setTimeout(() => convert(), 80);
          return;
        }
      }
    }
  });

  const start = () => observer.observe(document.body, { childList: true, subtree: true });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
